// Feedback trigger + modal sheet, mounted in the wizard header.
// Editorial register matching the Landing page (cream paper, serif
// body, oxblood submit). Posts to /feedback (access-code-gated,
// rate-limited) via the api helper; the operator dashboard surfaces
// submissions in the Feedback tab.
import { useEffect, useRef, useState, type FormEvent } from "react";

import { submitFeedback } from "../lib/api";

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "ok" }
  | { kind: "error"; message: string };

interface Props {
  server: string;
  accessCode: string;
}

export function FeedbackButton({ server, accessCode }: Props) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  // Refs for accessibility: restore focus to the trigger on close, and
  // for the auto-close timeout cleanup so a quick re-open doesn't get
  // force-closed by a stale timer.
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const modalRef = useRef<HTMLFormElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  function clearCloseTimer() {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }

  function close() {
    clearCloseTimer();
    setOpen(false);
    // Restore focus to the trigger so a keyboard user lands where they
    // started instead of at <body> top.
    triggerRef.current?.focus();
    // Don't clear body — if the user accidentally closed mid-send they
    // don't lose what they wrote. Reset on successful send only.
  }

  // Escape closes the modal; Tab/Shift-Tab cycles inside it (focus trap).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== "Tab" || !modalRef.current) return;
      // Collect focusables in DOM order; skip disabled/hidden ones. Cycle
      // shift-tab on first → last and tab on last → first so keyboard
      // focus can't escape to elements behind the overlay.
      const focusables = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex !== -1);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // close is stable enough for our use; missing dep would warn but
    // re-running on every render would re-attach the listener.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Autofocus the textarea on open.
  useEffect(() => {
    if (open) textareaRef.current?.focus();
  }, [open]);

  // Belt-and-suspenders timer cleanup on unmount.
  useEffect(() => () => clearCloseTimer(), []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    if (!accessCode) {
      // Don't bother hitting the server — the user landed here without
      // a valid code, which means the Landing → wizard handoff broke.
      // Surface a recovery instruction instead of echoing the server's
      // 401 message.
      setStatus({
        kind: "error",
        message:
          "Your session has expired. Reload the page and re-enter your access code.",
      });
      return;
    }
    setStatus({ kind: "submitting" });
    try {
      await submitFeedback({
        server,
        accessCode,
        feedback: {
          body: body.trim(),
          email: email.trim() || null,
        },
      });
      setStatus({ kind: "ok" });
      setBody("");
      setEmail("");
      // Auto-close after a successful send. Cleared if the user
      // re-opens before this fires so the modal doesn't snap shut on
      // them mid-edit.
      clearCloseTimer();
      closeTimerRef.current = window.setTimeout(() => {
        closeTimerRef.current = null;
        setOpen(false);
        setStatus({ kind: "idle" });
        triggerRef.current?.focus();
      }, 1200);
    } catch (e) {
      setStatus({
        kind: "error",
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }

  function openModal() {
    clearCloseTimer();
    setOpen(true);
    // Clear stale "Thanks — sent" if user re-opens immediately.
    if (status.kind === "ok") setStatus({ kind: "idle" });
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="feedback-trigger"
        onClick={openModal}
        aria-label="Send feedback to Aurora"
      >
        Send feedback
      </button>
      {open && (
        <div
          className="feedback-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="feedback-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <form ref={modalRef} className="feedback-modal" onSubmit={handleSubmit}>
            <h2 id="feedback-modal-title">How's this going?</h2>
            <p className="feedback-modal-lede">
              Anything that's confusing, broken, or could be better.
              We read every note.
            </p>

            <label htmlFor="feedback-body">Your note</label>
            <textarea
              ref={textareaRef}
              id="feedback-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="What's on your mind?"
              maxLength={4000}
              required
            />

            <label htmlFor="feedback-email">
              Email (optional — for follow-up)
            </label>
            <input
              id="feedback-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
            />

            {status.kind === "error" && (
              <p className="feedback-status is-error">{status.message}</p>
            )}
            {status.kind === "ok" && (
              <p className="feedback-status is-ok">Thanks — sent.</p>
            )}

            <div className="feedback-actions">
              <button
                type="button"
                className="ghost"
                onClick={close}
                disabled={status.kind === "submitting"}
              >
                Close
              </button>
              <button
                type="submit"
                className="primary"
                disabled={!body.trim() || status.kind === "submitting"}
              >
                {status.kind === "submitting" ? "Sending…" : "Send"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
